import { ApolloClient, NormalizedCacheObject } from "@apollo/client"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom"
import { EmptyView } from "../../components/EmptyView"
import { useFirebasePhotos } from "../../components/useFirebasePhotos"
import { useAppContext } from "../../contexts/AppContext"
import { isArrayNullOrEmpty } from "../../helper/helper"
import { ContractList } from "../../models/contract/ContractList"
import ContractDetail from "./ContractDetail"
import { DataContractsOpenned } from "./DataContractsOpenned"
import { DialogStatement } from "./component/DialogStatement"
import "./TodayDelivery.scss"
import { HourglassIcon, RouteIcon, TruckIcon } from "./TodayDeliveryIcons"
import { TodayDeliveryRow } from "./TodayDeliveryRow"
import { applyPhotoPositions, applyPhotoUrls, buildDeliveryRows, computeDeliveryKpis, truckPhotoUris } from "./todayDeliveryModel"

type Props = {
    client: ApolloClient<NormalizedCacheObject>
}

/**
 * Operational day view: the contracts delivered on a given market day.
 * Used to be the "current" tab of the purchases page, which is now a report.
 *
 * Rows navigate to a relative `contract_detail?id=...`, so this page has to
 * carry that nested route with it.
 */
export function TodayDeliveryPage({ client }: Props) {
    return (
        <Routes>
            <Route path="contract_detail" element={<ContractDetail client={client} />} />
            <Route path="/" element={<TodayDeliveryContent />} />
        </Routes>
    )
}

/** Shared by the header and every row, so the columns cannot drift apart. */
const TABLE_COLUMNS = '76px minmax(180px, 1.6fr) 78px minmax(100px, 0.85fr) minmax(124px, 1fr) minmax(74px, 0.7fr) 84px'

const NO_VALUE = '—'

type StatProps = {
    icon: React.ReactNode
    label: string
    value: string
    unit: string
}

const Stat: React.FC<StatProps> = ({ icon, label, value, unit }) => (
    <div className="ds-stat">
        <span className="ds-stat__icon">{icon}</span>
        <div className="ds-stat__body">
            <span className="ds-stat__label">{label}</span>
            <span className="ds-stat__value-row">
                <span className="ds-stat__value">{value}</span>
                <span className="ds-stat__unit">{unit}</span>
            </span>
        </div>
    </div>
)

function TodayDeliveryContent() {
    const { t } = useTranslation()
    const { millSelected } = useAppContext()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()
    const [marketDay, setMarketDay] = useState<Dayjs>(undefined)
    const [datePickerOpen, setDatePickerOpen] = useState(false)
    const [statementId, setStatementId] = useState<number>(undefined)
    const [contracts, setContracts] = useState<ContractList[]>([])

    // ne_contract_list takes one market day. Keep it in the URL like the tab, so
    // opening a contract and coming back restores the day instead of resetting it.
    useEffect(() => {
        if (millSelected) {
            const fromUrl = searchParams.get('market_day')
            setMarketDay(fromUrl ? dayjs(fromUrl) : dayjs().tz(millSelected.timezone ?? "Asia/Jakarta"))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [millSelected])

    const onMarketDaySelected = (newValue: Dayjs) => {
        setMarketDay(newValue)
        if (newValue?.isValid()) {
            searchParams.set('market_day', newValue.format("YYYY-MM-DD"))
            setSearchParams(searchParams, { replace: true })
        }
    }

    const day = marketDay?.format("YYYY-MM-DD")

    const millCoords = useMemo(() => {
        const coords = (millSelected as any)?.position?.coordinates
        return Array.isArray(coords) && coords.length === 2
            ? [Number(coords[0]), Number(coords[1])] as [number, number]
            : undefined
    }, [millSelected])

    const baseRows = useMemo(
        () => buildDeliveryRows(contracts, millCoords),
        [contracts, millCoords]
    )

    // Truck photos resolve after the contracts land: each one brings the list
    // thumbnail and, through its storage metadata, the truck-photo position.
    const photoUris = useMemo(() => truckPhotoUris(baseRows), [baseRows])
    const photos = useFirebasePhotos(photoUris)
    const resolvedCount = photoUris.filter(uri => photos.has(uri)).length

    const rows = useMemo(
        () => applyPhotoUrls(
            applyPhotoPositions(baseRows, uri => photos.get(uri)?.coordinates, millCoords),
            uri => photos.get(uri)?.url
        ),
        // `photos` is a stable ref; the resolved count is what changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseRows, millCoords, resolvedCount]
    )

    const kpis = computeDeliveryKpis(rows)

    const onContractDetailClick = (row: { contractId: number, statementId?: number, isNac: boolean }) => {
        const contract = contracts.find(c => c.id === row.contractId)
        if (!contract) return
        if (contract.isNac()) {
            setStatementId(contract.statement?.id)
        } else {
            navigate(`contract_detail?id=${contract.statement?.id}&contract_type=${contract.contract_type}`)
        }
    }

    const isEmpty = isArrayNullOrEmpty(rows)

    return (
        <div className="ds-page today-delivery">
            <div className="today-delivery__title-row">
                <h1 className="ds-page__title">{t('today_delivery')}</h1>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="ds-field today-delivery__day">
                        <span className="ds-field__label">{t('market_day')}</span>
                        <DatePicker
                            maxDate={dayjs()}
                            open={datePickerOpen}
                            onOpen={() => setDatePickerOpen(true)}
                            onClose={() => setDatePickerOpen(false)}
                            value={marketDay ?? null}
                            onChange={(newValue) => onMarketDaySelected(newValue)}
                        />
                    </div>
                </LocalizationProvider>
            </div>

            <div className="today-delivery__head">
                <div className="today-delivery__headings">
                    <span className="today-delivery__kicker">
                        {marketDay?.format('DD MMM YYYY').toUpperCase()} · {t(kpis.openCount > 0 ? 'gate_open' : 'gate_closed')}
                    </span>
                    <span className="today-delivery__subtitle">{t('today_delivery_subtitle')}</span>
                </div>

                <div className="today-delivery__stats">
                    <Stat
                        icon={<TruckIcon color="var(--color-primary)" />}
                        label={t('incoming_contracts')}
                        value={`${kpis.contractCount}`}
                        unit={t('in_yard')}
                    />
                    <Stat
                        icon={<RouteIcon color="var(--color-primary)" />}
                        label={t('avg_distance')}
                        value={kpis.avgDistanceKm === undefined ? NO_VALUE : kpis.avgDistanceKm.toFixed(1)}
                        unit={t('unit_km')}
                    />
                    <Stat
                        icon={<HourglassIcon color="var(--color-primary)" />}
                        label={t('still_open')}
                        value={`${kpis.openCount}`}
                        unit={t('contracts')}
                    />
                </div>
            </div>

            <DataContractsOpenned marketDay={day} onContractsFetch={setContracts} />

            {!isEmpty && (
                <div className="ds-grid-table" style={{ ['--ds-cols' as any]: TABLE_COLUMNS, ['--ds-min-width' as any]: '776px' }}>
                    <div className="ds-grid-table__head">
                        <span>{t('id')}</span>
                        <span>{t('wilayah')}</span>
                        <span style={{ textAlign: 'center' }}>{t('photo')}</span>
                        <span>{t('delivery')}</span>
                        <span>{t('status')}</span>
                        <span className="ds-grid-table__cell--num">{t('unit_rp_kg')}</span>
                        <span />
                    </div>
                    {rows.map(row => (
                        <TodayDeliveryRow
                            key={row.contractId}
                            row={row}
                            millCoords={millCoords}
                            onDetailClick={onContractDetailClick}
                        />
                    ))}
                </div>
            )}

            <div className="pempem-flex-row-center" style={{ marginTop: 10 }}>
                <EmptyView isVisible={isEmpty} type="clock" title={t('no_contract_filtered')} />
            </div>

            <DialogStatement statementId={statementId} isOpen={statementId !== undefined} onClose={() => setStatementId(undefined)} />
        </div>
    )
}
